// File: src/data/resultsScreenConfigs.js
// -----------------------------------------------------------------------------
// Configurations des écrans de résultats pour chaque questionnaire
// -----------------------------------------------------------------------------
import {
  generateSynthesis,
  generateRiasecSynthesis,
  generateJourdaSynthesis,
  generateImSynthesis,
  sendValuesAgreement,
  sendRiasecAgreement,
  sendJourdaAgreement,
  sendImAgreement
} from '../api/questionnaires';

// Labels pour le questionnaire Valeurs
const valueLabels = {
  liberte: {
    name: "Liberté",
    subtitle: "Penser et agir par toi-même",
    emoji: "🎯",
    color: "#6366f1",
    description: "Tu aimes prendre tes propres décisions, explorer tes idées, choisir ta voie. Tu n'apprécies pas qu'on te dise quoi faire ou penser. L'indépendance d'esprit est essentielle pour toi."
  },
  stimulation: {
    name: "Stimulation",
    subtitle: "Nouveauté, défis et aventure",
    emoji: "⚡",
    color: "#f59e0b",
    description: "Tu as besoin d'excitation, de variété, de challenges. La routine t'ennuie vite. Tu cherches des expériences qui te font vibrer et sortir de ta zone de confort."
  },
  plaisir: {
    name: "Plaisir",
    subtitle: "Profiter de la vie et des bons moments",
    emoji: "✨",
    color: "#ec4899",
    description: "Tu accordes de l'importance aux moments agréables, aux plaisirs simples ou intenses. Prendre du bon temps et profiter de la vie, c'est essentiel pour toi."
  },
  reussite: {
    name: "Réussite",
    subtitle: "Succès personnel et reconnaissance",
    emoji: "🏆",
    color: "#10b981",
    description: "Tu veux accomplir des choses et être reconnu pour tes compétences. Atteindre tes objectifs et démontrer ton excellence te motivent profondément."
  },
  pouvoir: {
    name: "Pouvoir",
    subtitle: "Influence, statut et autorité",
    emoji: "👑",
    color: "#8b5cf6",
    description: "Tu es attiré par les positions de leadership et le fait d'avoir de l'impact sur les décisions. Le statut social et le respect des autres comptent pour toi."
  },
  securite: {
    name: "Sécurité",
    subtitle: "Stabilité et environnement sûr",
    emoji: "🛡️",
    color: "#3b82f6",
    description: "Tu as besoin de te sentir en sécurité, de savoir que ton environnement est stable et ton avenir prévisible. L'incertitude te met mal à l'aise."
  },
  conformite: {
    name: "Conformité",
    subtitle: "Respect des règles et des autres",
    emoji: "🤝",
    color: "#06b6d4",
    description: "Tu trouves important de respecter les normes sociales et de ne pas déranger ou choquer les autres. Bien se comporter en société compte pour toi."
  },
  tradition: {
    name: "Tradition",
    subtitle: "Héritage culturel et familial",
    emoji: "🌳",
    color: "#84cc16",
    description: "Tu es attaché aux valeurs transmises par ta famille ou ta culture. Préserver cet héritage et le faire vivre à travers toi est important."
  },
  bienveillance: {
    name: "Bienveillance",
    subtitle: "Soin et loyauté envers tes proches",
    emoji: "💜",
    color: "#d946ef",
    description: "Le bien-être de tes proches est une priorité absolue. Tu es loyal, fiable, présent pour ceux que tu aimes. On peut compter sur toi."
  },
  universalisme: {
    name: "Universalisme",
    subtitle: "Justice, égalité et environnement",
    emoji: "🌍",
    color: "#14b8a6",
    description: "Tu crois en l'égalité de traitement pour tous, tu te soucies des plus faibles et de la protection de la planète. La justice sociale te tient à cœur."
  }
};

// Mapping des noms français vers les clés pour Valeurs
const valeursNameToKey = {
  "Liberté": "liberte",
  "Stimulation": "stimulation",
  "Plaisir": "plaisir",
  "Réussite": "reussite",
  "Pouvoir": "pouvoir",
  "Sécurité": "securite",
  "Conformité": "conformite",
  "Tradition": "tradition",
  "Bienveillance": "bienveillance",
  "Universalisme": "universalisme"
};

// Labels pour le questionnaire RIASEC
const riasecLabels = {
  R: {
    name: "Réaliste",
    subtitle: "Concret, manuel, technique",
    emoji: "🔧",
    color: "#10b981",
    description: "Tu aimes les activités concrètes où tu peux voir rapidement le résultat de ton travail. Habile de tes mains, tu préfères manipuler des objets, utiliser des outils ou des machines plutôt que de longues discussions. Tu es franc, pratique et endurant — le genre de personne sur qui on peut compter pour faire avancer les choses."
  },
  I: {
    name: "Investigateur",
    subtitle: "Analytique, curieux, scientifique",
    emoji: "🔬",
    color: "#8b5cf6",
    description: "Tu es curieux et tu aimes comprendre comment les choses fonctionnent. Analyser, observer, expérimenter pour résoudre des problèmes complexes : voilà ce qui te stimule. Indépendant et méthodique, tu préfères travailler seul et approfondir tes connaissances plutôt que diriger une équipe."
  },
  A: {
    name: "Artistique",
    subtitle: "Créatif, expressif, imaginatif",
    emoji: "🎨",
    color: "#f59e0b",
    description: "Tu as besoin de liberté pour exprimer ta créativité. Les activités trop routinières ou encadrées t'ennuient — tu préfères inventer, imaginer, créer à ta manière. Original et sensible, tu es attiré par tout ce qui touche à l'esthétique et à l'expression personnelle."
  },
  S: {
    name: "Social",
    subtitle: "Aide aux autres, coopération",
    emoji: "💜",
    color: "#ec4899",
    description: "Tu aimes être en contact avec les autres : aider, conseiller, enseigner, informer. Généreux et à l'écoute, tu es à l'aise dans les relations humaines et tu sais créer un climat de confiance. Les environnements chaleureux et participatifs te correspondent bien."
  },
  E: {
    name: "Entreprenant",
    subtitle: "Leadership, persuasion, ambition",
    emoji: "🚀",
    color: "#f97316",
    description: "Tu aimes les défis et tu donnes tout pour atteindre tes objectifs. Leader naturel, tu sais convaincre et entraîner les autres avec toi. Ambitieux et énergique, tu préfères prendre des décisions et mener des projets plutôt qu'exécuter des tâches répétitives."
  },
  C: {
    name: "Conventionnel",
    subtitle: "Organisation, méthode, précision",
    emoji: "📊",
    color: "#3b82f6",
    description: "Tu apprécies les environnements stables et bien organisés. Méthodique et rigoureux, tu aimes le travail bien fait, les procédures claires et les résultats précis. On peut compter sur toi pour gérer, classer et mener à bien les tâches avec fiabilité."
  }
};

const imLabels = {
  L: { 
    code: 'L', 
    name: 'Linguistique', 
    subtitle: 'L\'aisance avec les mots',
    emoji: '🗣️',
    color: '#6366F1',
    description: 'Tu as une aisance naturelle avec les mots. Lire, écrire, raconter, expliquer... le langage est ton terrain de jeu. Tu aimes jouer avec les mots, tu retiens facilement ce qui est dit, et tu sais convaincre ou émouvoir par la parole.',
    strengths: ['Écriture', 'Communication', 'Argumentation', 'Langues'],
    signesDetecteurs: [
      "S'exprime avec aisance",
      "Aime lire et raconter des histoires",
      "Aime débattre et jouer avec les mots",
      "Mémorise facilement noms, poésies, leçons",
      "Aime rédiger et composer"
    ],
    strategiesApprentissage: [
      "Parler de ce qui est appris, reformuler à voix haute",
      "Retaper ses notes de cours pour mieux les intégrer",
      "Faire un plan de la leçon, résumer avec ses propres mots",
      "Expliquer à quelqu'un d'autre pour ancrer les connaissances"
    ],
    activitesAdo: [
      "Jeux de société verbaux : Taboo, Time's Up, Dixit",
      "Aller au théâtre, écouter des humoristes",
      "Apprendre et pratiquer des langues étrangères",
      "Tenir un blog ou rédiger des articles",
      "Participer à des débats, développer ses arguments"
    ]
  },
  M: { 
    code: 'M', 
    name: 'Logico-mathématique', 
    subtitle: 'Le goût pour la logique',
    emoji: '🔢',
    color: '#10B981',
    description: 'Tu aimes comprendre comment les choses fonctionnent. Analyser, résoudre, déduire... la logique te guide. Tu es à l\'aise avec les chiffres, les raisonnements abstraits, et tu repères vite les incohérences.',
    strengths: ['Analyse', 'Résolution de problèmes', 'Raisonnement', 'Organisation'],
    signesDetecteurs: [
      "Aime compter et résoudre des problèmes",
      "Cherche les causes et les conséquences",
      "Aime les énigmes et les casse-têtes",
      "Rigoureux, va au bout de ce qu'il entreprend",
      "Aime les jeux de stratégie et organiser"
    ],
    strategiesApprentissage: [
      "Passer par le 'pourquoi' et le 'dans quel but'",
      "Classer, ordonner, structurer l'information",
      "Formuler des hypothèses et les vérifier",
      "Chercher la logique derrière les règles"
    ],
    activitesAdo: [
      "Jouer aux échecs ou jeux de stratégie",
      "Calculer un budget, évaluer des coûts (argent de poche, projet...)",
      "S'initier à un langage informatique (Python, JavaScript)",
      "Explorer la généalogie familiale ou l'astronomie",
      "Résoudre des énigmes, escape games"
    ]
  },
  S: { 
    code: 'S', 
    name: 'Spatiale', 
    subtitle: 'La pensée en images',
    emoji: '🎨',
    color: '#F59E0B',
    description: 'Tu penses en images. Visualiser, créer, transformer mentalement... l\'espace est ta toile. Tu te repères facilement, tu imagines les objets sous tous les angles, et tu as souvent besoin de dessiner pour expliquer.',
    strengths: ['Visualisation', 'Création graphique', 'Orientation', 'Design'],
    signesDetecteurs: [
      "Dessine avec couleurs et perspectives",
      "Sensible à l'esthétique",
      "Observateur, remarque les détails",
      "Se repère facilement, lit une carte",
      "Comprend mieux avec des schémas",
      "Aime les puzzles et les constructions"
    ],
    strategiesApprentissage: [
      "Travailler avec des couleurs, surligner, encadrer",
      "Dessiner, faire des croquis et des schémas",
      "Créer ses propres cartes mentales",
      "Se faire des images mentales, associer à des lieux"
    ],
    activitesAdo: [
      "Faire des maquettes, construire en 3D",
      "Imaginer un nouvel agencement de sa chambre",
      "Faire de la sculpture ou des arts plastiques",
      "S'initier aux logiciels de design (SketchUp, Blender, Canva)",
      "Apprendre à s'orienter avec cartes et boussole"
    ]
  },
  U: { 
    code: 'U', 
    name: 'Musicale', 
    subtitle: 'La sensibilité aux sons',
    emoji: '🎵',
    color: '#EC4899',
    description: 'Tu es sensible aux sons, aux rythmes, aux mélodies. La musique résonne en toi d\'une façon particulière. Tu retiens facilement les airs, tu perçois les nuances sonores, et la musique influence ton humeur.',
    strengths: ['Oreille musicale', 'Sens du rythme', 'Mémorisation sonore', 'Expression musicale'],
    signesDetecteurs: [
      "Aime chanter ou fredonner",
      "Danse ou bat le rythme naturellement",
      "Mémorise facilement les airs",
      "Reproduit ou crée des mélodies",
      "Reconnaît les voix et les sons",
      "Sensible aux intonations et ambiances sonores"
    ],
    strategiesApprentissage: [
      "Parler à voix haute, s'enregistrer et se réécouter",
      "Rythmer ce qui est à apprendre, le chantonner",
      "Utiliser la musique pour se concentrer ou s'énergiser",
      "Apprendre les langues grâce aux chansons"
    ],
    activitesAdo: [
      "Aller écouter des concerts et spectacles musicaux",
      "Faire partie d'une chorale, d'un orchestre ou d'un groupe",
      "Pratiquer la danse ou les percussions",
      "Créer des playlists thématiques",
      "S'initier à la production musicale (GarageBand, etc.)"
    ]
  },
  K: { 
    code: 'K', 
    name: 'Kinesthésique', 
    subtitle: 'L\'intelligence du corps',
    emoji: '🤸',
    color: '#EF4444',
    description: 'Ton corps est ton outil d\'expression et d\'apprentissage. Bouger, toucher, expérimenter... tu apprends en faisant. Tu as besoin de manipuler pour comprendre, et tu t\'exprimes naturellement par le mouvement.',
    strengths: ['Coordination', 'Habileté manuelle', 'Expression corporelle', 'Apprentissage pratique'],
    signesDetecteurs: [
      "Agile et bien coordonné",
      "Aime le sport, la danse ou le bricolage",
      "Aime construire, fabriquer, réparer",
      "Tactile, explore par le toucher",
      "Bouge pour expliquer ou réfléchir",
      "S'exprime par le corps et les gestes"
    ],
    strategiesApprentissage: [
      "Bouger en apprenant : marcher, manipuler un objet",
      "Intégrer des sensations physiques dans l'apprentissage",
      "Faire des jeux de rôles pour comprendre",
      "Expérimenter, manipuler, construire des maquettes"
    ],
    activitesAdo: [
      "Continuer le sport — essentiel à cet âge de transformation",
      "Bricoler, créer des objets à la maison",
      "Participer à des ateliers de théâtre ou d'improvisation",
      "Apprendre un art martial ou la danse",
      "Faire des stages pratiques, des immersions terrain"
    ]
  },
  E: { 
    code: 'E', 
    name: 'Interpersonnelle', 
    subtitle: 'La connexion aux autres',
    emoji: '👥',
    color: '#3B82F6',
    description: 'Tu comprends les autres intuitivement. Écouter, collaborer, fédérer... les relations humaines te nourrissent. Tu perçois les émotions des autres, tu sais créer du lien, et on vient naturellement te parler.',
    strengths: ['Empathie', 'Travail en équipe', 'Communication sociale', 'Leadership'],
    signesDetecteurs: [
      "Ouvert aux autres, aime discuter",
      "S'intègre facilement dans un groupe",
      "Sensible aux émotions des autres",
      "Aime aider et participer",
      "Fédère naturellement autour de lui",
      "Apprécié pour son écoute"
    ],
    strategiesApprentissage: [
      "Privilégier le travail en groupe, partager, comparer",
      "Réviser à deux, se faire réciter",
      "Expliquer aux autres pour ancrer ses connaissances",
      "Demander des explications, interagir avec les profs"
    ],
    activitesAdo: [
      "Pratiquer l'écoute active dans les conversations",
      "Participer à des débats structurés",
      "S'engager dans des projets collectifs : bénévolat, scoutisme",
      "Participer à des colonies ou camps encadrés",
      "Organiser des événements entre amis"
    ]
  },
  I: { 
    code: 'I', 
    name: 'Intrapersonnelle', 
    subtitle: 'La connaissance de soi',
    emoji: '🧘',
    color: '#8B5CF6',
    description: 'Tu te connais bien et prends le temps de réfléchir. L\'introspection et l\'autonomie sont tes forces. Tu sais identifier tes émotions, tu as besoin de moments seul pour te ressourcer, et tu réfléchis avant d\'agir.',
    strengths: ['Connaissance de soi', 'Réflexion', 'Autonomie', 'Gestion des émotions'],
    signesDetecteurs: [
      "Calme et posé, réfléchit avant d'agir",
      "Travaille de manière autonome",
      "Conscient de ses émotions",
      "Sait s'autoévaluer",
      "Besoin de solitude pour réfléchir",
      "Forte vie intérieure"
    ],
    strategiesApprentissage: [
      "Travailler seul, à son propre rythme",
      "Disposer d'un espace personnel calme",
      "Se fixer ses propres objectifs et s'auto-évaluer",
      "Prendre du temps pour réfléchir avant d'agir"
    ],
    activitesAdo: [
      "Pratiquer la méditation ou le yoga",
      "Tenir un journal personnel ou un blog privé",
      "Décrypter ses propres mécanismes psychologiques",
      "Faire des bilans réguliers : forces, progrès, objectifs",
      "Lire des livres de développement personnel adaptés aux ados"
    ]
  },
  N: { 
    code: 'N', 
    name: 'Naturaliste', 
    subtitle: 'La connexion au vivant',
    emoji: '🌿',
    color: '#22C55E',
    description: 'Tu es connecté au vivant et à l\'environnement. Observer, classer, protéger... la nature t\'appelle. Tu remarques les détails dans ton environnement, tu te sens bien en plein air, et l\'écologie te tient à cœur.',
    strengths: ['Observation', 'Sensibilité écologique', 'Classification', 'Connexion au vivant'],
    signesDetecteurs: [
      "Heureux dans la nature",
      "Passionné par les animaux ou les plantes",
      "Aime jardiner ou observer",
      "Se soucie de l'environnement",
      "Observateur des détails naturels",
      "Aime collectionner et classer"
    ],
    strategiesApprentissage: [
      "Expérimenter avec des éléments naturels",
      "Organiser l'information en tableaux, classifications",
      "Développer sa curiosité par l'observation",
      "Relier les apprentissages à des exemples concrets dans la nature"
    ],
    activitesAdo: [
      "Créer un jardin, un potager ou s'occuper d'animaux",
      "Observer les étoiles, la météo, les phénomènes naturels",
      "S'investir dans des associations de protection de la nature",
      "Faire du bénévolat dans une réserve naturelle",
      "Sensibiliser son entourage aux économies d'énergie, au recyclage"
    ]
  }
};


// Configuration pour les résultats Valeurs
export const valeursResultsConfig = {
  // Labels des dimensions
  labels: valueLabels,

  // Mapping nom -> clé
  nameToKey: valeursNameToKey,

  // Titres des sections
  pageTitle: "Ce qui compte pour toi",
  topSectionTitle: "Tes 3 valeurs clés",
  allSectionTitle: "Toutes tes valeurs",
  allSectionSubtitle: "Appuie sur une valeur pour en savoir plus",
  synthesisSectionTitle: "Ce que ça révèle",

  attribution: "Basé sur la théorie des Valeurs de Shalom Schwartz\nOutil d'exploration • Pas un diagnostic",
  disclaimer: "Ces résultats reflètent ce qui compte pour toi aujourd'hui, pas une identité figée. Tes valeurs se précisent avec tes expériences de vie. C'est un point de départ pour explorer ce qui te correspond le mieux.",

  // Nombre de top items à afficher
  topCount: 3,

  // Fonction pour extraire les données des résultats
  processResults: (initialResults) => {
    if (!initialResults?.results?.centeredScores) return [];

    const resultsObj =
      typeof initialResults.results.centeredScores === 'string'
        ? JSON.parse(initialResults.results.centeredScores)
        : initialResults.results.centeredScores;

    const entries = Object.entries(resultsObj)
      .map(([name, score]) => {
        const key = valeursNameToKey[name];
        const label = valueLabels[key] || {};
        return {
          key,
          name: label.name || name,
          score,
          emoji: label.emoji || "❓",
          color: label.color || "#6b7280",
          subtitle: label.subtitle || "",
          description: label.description || ""
        };
      });

    // Normalisation min/max
    const scores = entries.map(v => v.score);
    const min = Math.min(...scores);
    const max = Math.max(...scores);

    return entries
      .map((value) => ({
        ...value,
        normalizedScore: min === max ? 50 : ((value.score - min) / (max - min)) * 100
      }))
      .sort((a, b) => b.score - a.score)
      .map((value, index) => ({
        ...value,
        rank: index + 1
      }));
  },

  // Fonction pour générer la synthèse
  generateSynthesis: generateSynthesis,

  // Clé pour accéder à la synthèse dans les résultats
  synthesisKey: 'synthesis',

  // Nom de la variable LUX pour cette synthèse
  luxVariableName: 'synthese_valeurs_ado',

  // Fonction pour envoyer l'accord de partage aux parents
  sendAgreement: sendValuesAgreement
};

// Configuration pour les résultats RIASEC
export const riasecResultsConfig = {
  // Labels des dimensions
  labels: riasecLabels,

  // Pas de mapping nécessaire, les clés sont directes (R, I, A, S, E, C)
  nameToKey: null,

  // Titres des sections
  pageTitle: "Ce qui t'attire",
  topSectionTitle: "Tes 3 univers préférés",
  allSectionTitle: "Tous tes univers",
  allSectionSubtitle: "Appuie sur un univers pour en savoir plus",
  synthesisSectionTitle: "Ce que ça révèle",

  attribution: "Basé sur la théorie RIASEC de John Holland\nOutil d'exploration • Pas un diagnostic",
  disclaimer: "Ces résultats reflètent ce qui t'attire aujourd'hui, pas un destin professionnel. Tes intérêts évoluent quand tu découvres de nouvelles activités. C'est un point de départ pour explorer ce qui te correspond le mieux.",

  // Nombre de top items à afficher
  topCount: 3,

  // Fonction pour extraire les données des résultats
  processResults: (initialResults) => {
    if (!initialResults?.results?.dimensions) return [];

    return initialResults.results?.dimensions.map((dim) => {
      const label = riasecLabels[dim.key] || {};
      return {
        key: dim.key,
        name: label.name || dim.key,
        score: dim.score,
        rank: dim.rank,
        emoji: label.emoji || "❓",
        color: label.color || "#6b7280",
        subtitle: label.subtitle || "",
        description: label.description || "",
        // Normalisation sur 25 (score max possible)
        normalizedScore: (dim.score / 25) * 100
      };
    });
  },

  // Fonction pour générer la synthèse
  generateSynthesis: generateRiasecSynthesis,

  // Clé pour accéder à la synthèse dans les résultats
  synthesisKey: 'synthesis',

  // Nom de la variable LUX pour cette synthèse
  luxVariableName: 'synthese_interets_ado',

  // Code Holland (spécifique RIASEC)
  getHollandCode: (results) => results?.results?.hollandCode || '',

  // Fonction pour envoyer l'accord de partage aux parents
  sendAgreement: sendRiasecAgreement
};

// Configuration pour les résultats Jourda
// Note: Les labels viennent de l'API car les dimensions sont dynamiques
export const jourdaResultsConfig = {
  // Pas de labels statiques - ils viennent de l'API
  labels: null,

  // Pas de mapping nécessaire
  nameToKey: null,

  // Titres des sections
  pageTitle: "Comment tu fonctionnes",
  topSectionTitle: "Ton profil",
  allSectionTitle: "Tes 4 moteurs",
  allSectionSubtitle: "Appuie sur un moteur pour en savoir plus",
  synthesisSectionTitle: "Ce que ça révèle",

  attribution: "Basé sur la méthode des énergies professionnelles de Robert Jourda\nOutil d'exploration • Pas un diagnostic",
  disclaimer: "Ces résultats reflètent comment tu fonctionnes aujourd'hui, pas une personnalité figée. Ton mode de fonctionnement peut s'adapter selon les contextes. C'est un point de départ pour explorer ce qui te correspond le mieux.",

  // Nombre de top items à afficher
  topCount: 3,

  // Fonction pour extraire les données des résultats
  // Les données viennent directement de l'API avec le format attendu
  processResults: (initialResults) => {
    if (!initialResults?.results?.dimensions) return [];

    return initialResults.results.dimensions.map((dim, index) => ({
      key: dim.key || dim.id,
      name: dim.name || dim.label,
      score: dim.score,
      rank: dim.rank || index + 1,
      emoji: dim.emoji || "🎯",
      color: dim.color || "#6366f1",
      subtitle: dim.subtitle || "",
      description: dim.description || "",
      normalizedScore: dim.normalizedScore || (dim.score / 100) * 100
    }));
  },

  // Fonction pour générer la synthèse
  generateSynthesis: generateJourdaSynthesis,

  // Clé pour accéder à la synthèse dans les résultats
  synthesisKey: 'synthesis',

  // Nom de la variable LUX pour cette synthèse
  luxVariableName: 'synthese_energies_ado'
};

// Mapping des noms français vers les clés pour IM
const imNameToKey = {
  "Linguistique": "L",
  "Logico-mathématique": "M",
  "Spatiale": "S",
  "Musicale": "U",
  "Kinesthésique": "K",
  "Interpersonnelle": "E",
  "Intrapersonnelle": "I",
  "Naturaliste": "N"
};

// Configuration pour les résultats Intelligences Multiples
export const imResultsConfig = {
  // Labels des dimensions
  labels: imLabels,

  // Mapping nom -> clé
  nameToKey: imNameToKey,

  // Titres des sections
  pageTitle: "Tes intelligences",
  topSectionTitle: "Tes 3 intelligences dominantes",
  allSectionTitle: "Toutes tes intelligences",
  allSectionSubtitle: "Appuie sur une intelligence pour en savoir plus",
  synthesisSectionTitle: "Ce que ça révèle",
  
  attribution: "Basé sur la théorie des Intelligences Multiples de Howard Gardner\nOutil d'exploration • Pas un diagnostic",
  disclaimer: "Ces résultats reflètent tes préférences actuelles, pas des capacités figées. Tes talents évoluent avec tes expériences. C'est un point de départ pour explorer ce qui te correspond le mieux.",
  // Nombre de top items à afficher
  topCount: 3,

  // Score maximum pour les barres de progression
  maxScore: 20,

  // Fonction pour extraire les données des résultats
  // Format API: results.intelligences = [{key, score, maxScore, rank, code, name}, ...]
  processResults: (initialResults) => {
    if (!initialResults?.results?.intelligences) return [];

    const intelligences = initialResults.results.intelligences;
    const maxScore = initialResults.results.maxScore || 20;

    return intelligences.map((intel) => {
      const label = imLabels[intel.key] || {};
      return {
        key: intel.key,
        code: intel.code,
        name: label.name || intel.name,
        score: intel.score,
        maxScore: intel.maxScore || maxScore,
        rank: intel.rank,
        emoji: label.emoji || "🧠",
        color: label.color || "#6b7280",
        subtitle: label.subtitle || "",
        description: label.description || "",
        strengths: label.strengths || [],
        signesDetecteurs: label.signesDetecteurs || [],
        strategiesApprentissage: label.strategiesApprentissage || [],
        activitesAdo: label.activitesAdo || [],
        // Score normalisé sur 100 pour les barres de progression
        normalizedScore: (intel.score / (intel.maxScore || maxScore)) * 100
      };
    });
  },

  // Fonction pour générer la synthèse
  generateSynthesis: generateImSynthesis,

  // Clé pour accéder à la synthèse dans les résultats
  synthesisKey: 'synthesis',

  // Nom de la variable LUX pour cette synthèse
  luxVariableName: 'synthese_intelligences_ado',

  // Fonction pour envoyer l'accord de partage aux parents
  sendAgreement: sendImAgreement
};
